import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { ChevronDown, X } from 'lucide-react';

/**
 * Combobox: free-typing input with autocomplete suggestions from a list of options.
 * - User can type anything (free-form value)
 * - Or pick from suggestions matching their input
 * - Selecting from the list fills the field
 */
export const Combobox = ({ value, onChange, options = [], placeholder, allowClear = true, className = '' }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [hovered, setHovered] = useState(-1);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = (value || '').trim()
    ? options.filter((o) => o.toLowerCase().includes(value.toLowerCase()))
    : options;

  const pick = (v) => { onChange(v); setOpen(false); };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHovered((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHovered((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && open && hovered >= 0 && filtered[hovered]) { e.preventDefault(); pick(filtered[hovered]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Input
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHovered(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
      />
      <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
        {allowClear && value && (
          <button type="button" onMouseDown={(e) => { e.preventDefault(); onChange(''); }} className="p-1 hover:bg-slate-100 rounded">
            <X className="h-3.5 w-3.5 text-slate-400" />
          </button>
        )}
        <button type="button" onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }} className="p-1 hover:bg-slate-100 rounded">
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">
              {value?.trim() ? (
                <>No matches — <span className="font-semibold text-[hsl(217,91%,24%)]">"{value}"</span> will be saved as custom value.</>
              ) : (
                'No options yet. Type to add a new value.'
              )}
            </div>
          ) : (
            <ul className="py-1">
              {filtered.map((o, i) => (
                <li key={o}>
                  <button
                    type="button"
                    onMouseEnter={() => setHovered(i)}
                    onClick={() => pick(o)}
                    className={`w-full text-left px-3 py-1.5 text-sm ${i === hovered ? 'bg-blue-50 text-[hsl(217,91%,24%)]' : 'hover:bg-slate-50'}`}
                  >
                    {o}
                  </button>
                </li>
              ))}
              {value?.trim() && !filtered.some((o) => o.toLowerCase() === value.toLowerCase()) && (
                <li className="border-t">
                  <div className="px-3 py-1.5 text-xs text-slate-500">
                    Press <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">Enter</span> or save to keep custom value <span className="font-semibold text-[hsl(217,91%,24%)]">"{value}"</span>
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default Combobox;
